import { Link } from 'react-router-dom';
import { ArrowRight, Check, CircleCheckBig, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

type Profile = 'conductor' | 'autonomo' | 'flota' | 'academia';

const profiles: Record<Profile, {
  eyebrow: string;
  title: string;
  lead: string;
  primary: string;
  primaryTo: string;
  secondary: string;
  secondaryTo: string;
  benefits: string[];
  steps: [string, string][];
}> = {
  conductor: {
    eyebrow: 'Para conductores VTC',
    title: 'Prepárate entre servicios, sin volver a un aula',
    lead: 'Sesiones cortas desde el móvil, simulacros con la estructura oficial y un panel que señala el módulo que te puede dejar no apto.',
    primary: 'Hacer mi primer simulacro', primaryTo: '/simulacro',
    secondary: 'Crear cuenta gratis', secondaryTo: '/cuenta/registro',
    benefits: ['187 preguntas oficiales', 'Racha y progreso por módulo', 'Repaso automático de fallos', 'Explicaciones claras en español'],
    steps: [['Diagnóstico', 'Haz un simulacro de 60 preguntas.'], ['Prioridad', 'Detectamos el módulo por debajo del 50%.'], ['Mejora', 'Practica diez preguntas cada vez.']],
  },
  autonomo: {
    eyebrow: 'Para conductores autónomos',
    title: 'Tu preparación, a tu horario y con un único pago',
    lead: 'Sin matrículas, desplazamientos ni cuotas mensuales. Estudia cuando tengas una pausa y conserva el progreso en cualquier dispositivo.',
    primary: 'Ver acceso individual', primaryTo: '/precios',
    secondary: 'Probar el simulacro', secondaryTo: '/simulacro',
    benefits: ['Pago único, sin renovación', 'Acceso desde móvil y ordenador', 'Simulacros ilimitados', 'Datos exportables y cuenta borrable'],
    steps: [['Prueba', 'Comprueba tu nivel sin registrarte.'], ['Activa', 'Desbloquea el banco completo una sola vez.'], ['Acredita', 'Llega a la convocatoria sabiendo que superas los cuatro módulos.']],
  },
  flota: {
    eyebrow: 'Para empresas y flotas VTC',
    title: 'Sabe quién está preparado sin perseguir hojas de cálculo',
    lead: 'Invita a tus conductores, asigna asientos y detecta quién no ha empezado o sigue bajo el corte, siempre con consentimiento visible.',
    primary: 'Calcular licencias', primaryTo: '/precios?plan=flota',
    secondary: 'Ver cómo funciona', secondaryTo: '/flota',
    benefits: ['Precio por conductor', 'Invitaciones por correo', 'Semáforo de riesgo por módulos', 'CSV exportable y consentimiento revocable'],
    steps: [['Compra asientos', 'Elige solo las licencias que necesitas.'], ['Invita', 'Cada conductor acepta qué datos comparte.'], ['Acompaña', 'Prioriza a quien no llega al 50% en algún módulo.']],
  },
  academia: {
    eyebrow: 'Para academias y centros de formación',
    title: 'Añade práctica digital a tu formación presencial',
    lead: 'Un entorno de entrenamiento por módulos para que el alumno practique fuera del aula y el formador pueda centrar la tutoría donde hay riesgo.',
    primary: 'Solicitar propuesta', primaryTo: '/precios?plan=academia',
    secondary: 'Explorar el banco', secondaryTo: '/simulacro',
    benefits: ['Grupos de alumnos', 'Seguimiento individual consentido', 'Simulacros con estructura oficial', 'Material por temas y formatos'],
    steps: [['Crea el grupo', 'Configuramos los asientos del curso.'], ['Asigna práctica', 'Cada alumno estudia desde su móvil.'], ['Refuerza', 'Usa el panel para decidir qué explicar en clase.']],
  },
};

export function ProfileLandingPage({ profile }: { profile: Profile }) {
  const content = profiles[profile];
  return (
    <>
      <section className="border-b bg-muted/35">
        <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-24">
          <p className="text-sm font-semibold uppercase tracking-[0.16em] text-primary">{content.eyebrow}</p>
          <h1 className="mt-4 max-w-4xl text-balance text-4xl font-semibold tracking-[-0.035em] sm:text-5xl">{content.title}</h1>
          <p className="mt-6 max-w-3xl text-xl leading-8 text-muted-foreground">{content.lead}</p>
          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <Button render={<Link to={content.primaryTo} />} size="lg">{content.primary} <ArrowRight /></Button>
            <Button render={<Link to={content.secondaryTo} />} size="lg" variant="outline">{content.secondary}</Button>
          </div>
        </div>
      </section>
      <section className="mx-auto grid max-w-6xl gap-10 px-4 py-16 sm:px-6 lg:grid-cols-2 lg:py-20">
        <div>
          <h2 className="text-3xl font-semibold tracking-tight">Todo lo necesario, sin ruido</h2>
          <div className="mt-7 grid gap-4">
            {content.benefits.map((benefit) => (
              <div key={benefit} className="flex items-center gap-3 rounded-xl border bg-card p-4 shadow-soft">
                <CircleCheckBig className="size-5 shrink-0 text-success" /><span className="font-medium">{benefit}</span>
              </div>
            ))}
          </div>
        </div>
        <Card className="shadow-raised">
          <CardHeader><ShieldCheck className="size-7 text-primary" /><CardTitle className="mt-4 text-2xl">Cómo funciona</CardTitle></CardHeader>
          <CardContent className="grid gap-6">
            {content.steps.map(([title, text], index) => (
              <div key={title} className="grid grid-cols-[2.5rem_1fr] gap-4">
                <span className="grid size-10 place-items-center rounded-xl bg-primary text-sm font-bold text-primary-foreground">{index + 1}</span>
                <div><p className="font-semibold">{title}</p><p className="mt-1 text-sm leading-6 text-muted-foreground">{text}</p></div>
              </div>
            ))}
          </CardContent>
        </Card>
      </section>
      <section className="border-y bg-primary text-primary-foreground">
        <div className="mx-auto flex max-w-6xl flex-col items-start justify-between gap-7 px-4 py-12 sm:px-6 md:flex-row md:items-center">
          <div><h2 className="text-2xl font-semibold">Empieza con datos, no con suposiciones</h2><p className="mt-2 text-primary-foreground/75">El simulacro inicial es gratuito y reproduce los cuatro módulos.</p></div>
          <Button render={<Link to={content.primaryTo} />} variant="secondary" size="lg">{content.primary} <Check /></Button>
        </div>
      </section>
    </>
  );
}
